import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

const RoleAccessLayer = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // For pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rolesPerPage, setRolesPerPage] = useState(10);
    const [filterStatus, setFilterStatus] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Fetch roles from API
    const fetchRoles = async () => {
        try {
            setLoading(true);
            const data = await api.roles.getAll();
            
            // Transform data to include active status and description for display purposes
            const rolesWithMeta = data.data.map(role => ({
                id: role.name,
                name: formatRoleName(role.name),
                description: `This is the ${formatRoleName(role.name)} role description`,
                isActive: true, // All enum roles are considered active
                userCount: role.userCount,
                createdAt: new Date().toISOString() // Add default date for display
            }));
            
            setRoles(rolesWithMeta);
            setLoading(false);
        } catch (err) {
            setError(`Failed to fetch roles: ${err.message}`);
            setLoading(false);
            console.error('Error fetching roles:', err);
            
            // Set mock data as fallback
            const mockRoles = [
                { id: 'USER', name: 'User', description: 'Regular user role', isActive: true, userCount: 10, createdAt: new Date().toISOString() },
                { id: 'ADMIN', name: 'Admin', description: 'Administrator role', isActive: true, userCount: 2, createdAt: new Date().toISOString() },
                { id: 'INSTRUCTOR', name: 'Instructor', description: 'Instructor role', isActive: true, userCount: 5, createdAt: new Date().toISOString() }
            ];
            setRoles(mockRoles);
        }
    };

    // Format role name for display (convert USER to User, GAME_DEVELOPER to Game Developer)
    const formatRoleName = (roleName) => {
        return roleName
            .split('_')
            .map(word => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Parse role name for API (convert "Game Developer" to GAME_DEVELOPER)
    const parseRoleName = (displayName) => {
        return displayName
            .toUpperCase()
            .replace(/ /g, '_');
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    // Filter roles based on search and status
    const filteredRoles = roles.filter(role => {
        const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              (role.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        
        if (filterStatus === 'All') return matchesSearch;
        if (filterStatus === 'Active') return matchesSearch && role.isActive;
        if (filterStatus === 'Inactive') return matchesSearch && !role.isActive;
        
        return matchesSearch;
    });

    // Pagination
    const indexOfLastRole = currentPage * rolesPerPage;
    const indexOfFirstRole = indexOfLastRole - rolesPerPage;
    const currentRoles = filteredRoles.slice(indexOfFirstRole, indexOfLastRole);
    const totalPages = Math.ceil(filteredRoles.length / rolesPerPage);

    // Handle page change
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Get formatted date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <span className="text-md fw-medium text-secondary-light mb-0">
                            Show
                        </span>
                        <select 
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                            value={rolesPerPage}
                            onChange={(e) => setRolesPerPage(parseInt(e.target.value))}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                        </select>
                        <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                name="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>
                        <select 
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                        >
                            <option value="All">All Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </div>
                </div>
                <div className="card-body p-24">
                    {error && (
                        <div className="alert alert-danger">{error}</div>
                    )}
                    {loading ? (
                        <div className="d-flex justify-content-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">
                                            <div className="d-flex align-items-center gap-10">
                                                <div className="form-check style-check d-flex align-items-center">
                                                    <input
                                                        className="form-check-input radius-4 border input-form-dark"
                                                        type="checkbox"
                                                        name="checkbox"
                                                        id="selectAll"
                                                    />
                                                </div>
                                                S.L
                                            </div>
                                        </th>
                                        <th scope="col">Create Date</th>
                                        <th scope="col">Role</th>
                                        <th scope="col">Description</th>
                                        <th scope="col" className="text-center">
                                            User Count
                                        </th>
                                        <th scope="col" className="text-center">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRoles.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center">No roles found</td>
                                        </tr>
                                    ) : (
                                        currentRoles.map((role, index) => (
                                            <tr key={role.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-10">
                                                        <div className="form-check style-check d-flex align-items-center">
                                                            <input
                                                                className="form-check-input radius-4 border border-neutral-400"
                                                                type="checkbox"
                                                                name="checkbox"
                                                            />
                                                        </div>
                                                        {indexOfFirstRole + index + 1}
                                                    </div>
                                                </td>
                                                <td>{formatDate(role.createdAt)}</td>
                                                <td>{role.name}</td>
                                                <td>
                                                    <p className="max-w-500-px">
                                                        {role.description || 'No description provided'}
                                                    </p>
                                                </td>
                                                <td className="text-center">
                                                    {role.userCount || 0}
                                                </td>
                                                <td className="text-center">
                                                    <span className={`${role.isActive 
                                                        ? 'bg-success-focus text-success-600 border border-success-main' 
                                                        : 'bg-danger-focus text-danger-600 border border-danger-main'} px-24 py-4 radius-4 fw-medium text-sm`}>
                                                        {role.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                        <span>Showing {indexOfFirstRole + 1} to {Math.min(indexOfLastRole, filteredRoles.length)} of {filteredRoles.length} entries</span>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <Link
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                                    to="#"
                                    onClick={() => paginate(currentPage - 1)}
                                >
                                    <Icon icon="ep:d-arrow-left" className="" />
                                </Link>
                            </li>
                            {[...Array(totalPages).keys()].map(number => (
                                <li key={number + 1} className="page-item">
                                    <Link
                                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === number + 1 ? 'bg-primary-600 text-white' : 'bg-neutral-200 text-secondary-light'}`}
                                        to="#"
                                        onClick={() => paginate(number + 1)}
                                    >
                                        {number + 1}
                                    </Link>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <Link
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                                    to="#"
                                    onClick={() => paginate(currentPage + 1)}
                                >
                                    <Icon icon="ep:d-arrow-right" className="" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoleAccessLayer;